<template>
	<a-modal
		class="g-image-quad-edit-modal"
		:visible="visible"
		title="图片模块编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
		<div class="g-image-quad-edit" @click="clearSelection">
			<div class="g-image-quad-edit__actions" @click.stop>
				<span v-if="form.items.length > 1" class="g-image-quad-edit__hint">图片支持拖拽排序</span>
				<div class="g-image-quad-edit__actions-btns">
					<button type="button" class="g-image-quad-edit__batch-btn" @click="addBatch">添加一批</button>
					<button type="button" class="g-image-quad-edit__batch-btn" @click="addOne">添加一格</button>
				</div>
			</div>

			<EditListSortGroup
				ref="editGrid"
				class="g-image-quad-edit__grid"
				:class="{
					'is-inset': !form.fullWidth,
				}"
				@dragover="onSortDragOver"
			>
				<div
					v-for="(item, index) in form.items"
					:key="item.id"
					:data-sort-id="item.id"
					class="g-image-quad-edit__cell-wrap"
					:class="[{ 'is-active': selectedIndex === index }, getEditListSortItemClass(dragId, item.id, dragOverId)]"
					@click.stop
				>
					<div
						class="g-image-quad-edit__cell"
						:class="{ 'is-selected': selectedIndex === index }"
						:draggable="form.items.length > 1"
						@click="selectItem(index)"
						@dragstart="onItemDragStart(item.id)"
						@dragend="onItemDragEnd"
					>
						<div
							class="g-image-quad-edit__thumb"
							:style="previewThumbStyle"
						>
							<UploadProgressPanel
								v-if="isUploading && selectedIndex === index"
								:tasks="uploadTasks"
								variant="item"
								hide-filename
							/>
							<template v-else>
								<img v-if="getImageUrl(item)" :src="getImageUrl(item)" alt="" />
								<i v-else class="mbm-iconfont mbm-tupian"></i>
							</template>
						</div>
					</div>

					<div
						v-if="selectedIndex === index"
						class="g-image-quad-edit__panel g-image-quad-edit__panel--arrow g-image-quad-edit__panel--float"
						:class="getPanelAlignClass(index)"
						@click.stop
					>
						<div class="g-image-quad-edit__toolbar">
							<button type="button" class="g-image-quad-edit__action" @click="triggerImageSelect">换图</button>
							<button
								type="button"
								class="g-image-quad-edit__action"
								:class="{ 'is-active': activePanel === 'height' }"
								@click="togglePanel('height')"
							>高度</button>
							<button
								type="button"
								class="g-image-quad-edit__action"
								:class="{ 'is-active': form.fullWidth }"
								@click="toggleFullWidth"
							>左右铺满[{{ form.fullWidth ? '是' : '否' }}]</button>
							<button
								type="button"
								class="g-image-quad-edit__action"
								:class="{ 'is-active': activePanel === 'radius' }"
								@click="togglePanel('radius')"
							>圆角</button>
							<button
								type="button"
								class="g-image-quad-edit__action g-image-quad-edit__action--danger-text"
								:disabled="form.items.length <= 1"
								@click="deleteItem(index)"
							>删除</button>
						</div>
						<div v-if="activePanel === 'height'" class="g-image-quad-edit__sub-row">
							<span class="g-image-quad-edit__sub-label">高度 (px)</span>
							<a-input-number
								v-model:value="heightDraft"
								:min="1"
								:max="800"
								:precision="0"
								class="g-image-quad-edit__sub-input"
							/>
							<button type="button" class="g-image-quad-edit__sub-confirm" @click="applyHeight">确定</button>
							<span class="g-image-quad-edit__sub-default">默认值:{{ defaultHeight }}</span>
						</div>
						<div v-if="activePanel === 'radius'" class="g-image-quad-edit__sub-row">
							<span class="g-image-quad-edit__sub-label">圆角 (px)</span>
							<a-input-number
								v-model:value="radiusDraft"
								:min="0"
								:max="100"
								:precision="0"
								class="g-image-quad-edit__sub-input"
							/>
							<button type="button" class="g-image-quad-edit__sub-confirm" @click="applyRadius">确定</button>
						</div>
					</div>
				</div>
			</EditListSortGroup>
		</div>
		</div>

		<input
			ref="imageInput"
			type="file"
			accept="image/*"
			class="g-image-quad-edit__file-input"
			@change="onImageSelect"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" class="g-image-quad-edit-modal__ok" :disabled="isUploading" @click="onConfirm">确定</a-button>
		</template>
	</a-modal>
</template>

<script>
	import {
		isLocalImageFile,
		MAX_LOCAL_IMAGE_SIZE,
		revokeLocalImageUrl,
	} from '../utils/localImage.js';
	import {
		createDefaultImageQuadItem,
		createDefaultImageQuadItems,
		getImageQuadImageUrl,
		getImageQuadThumbAspectRatio,
		IMAGE_QUAD_DEFAULT_HEIGHT,
		normalizeImageQuadData,
	} from '../utils/imageQuad.js';
	import { ensureEditFloatPanelVisible, getGridPanelAlignClass } from '../utils/editPanelAlign.js';
	import EditListSortGroup from './EditListSortGroup.vue';
	import {
		createEditListDragState,
		getEditListSortItemClass,
		handleEditListSortDragEnd,
		handleEditListSortDragOver,
		handleEditListSortDragStart,
		resolveSelectedIndexAfterListMove,
	} from '../utils/editListDragSort.js';
	import { replaceQrcodeNavCellImage } from '../utils/qrcodeNav.js';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GImageQuadEditModal',
		components: { EditListSortGroup, UploadProgressPanel },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => ({}),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: normalizeImageQuadData({}),
				selectedIndex: null,
				activePanel: '',
				heightDraft: IMAGE_QUAD_DEFAULT_HEIGHT,
				radiusDraft: 4,
				defaultHeight: IMAGE_QUAD_DEFAULT_HEIGHT,
				isUploading: false,
				uploadTasks: [],
				...createEditListDragState(),
			};
		},
		computed: {
			selectedItem() {
				return this.form.items[this.selectedIndex] || null;
			},
			previewThumbStyle() {
				return {
					aspectRatio: getImageQuadThumbAspectRatio(this.form.imageHeight, true),
					borderRadius: `${this.form.borderRadius}px`,
				};
			},
		},
		watch: {
			visible(val) {
				if (val) {
					this.selectedIndex = null;
					this.activePanel = '';
					this.syncForm();
				}
			},
			data: {
				deep: true,
				handler() {
					if (this.visible) {
						this.syncForm();
					}
				},
			},
		},
		methods: {
			getEditListSortItemClass,
			getPanelAlignClass(index) {
				return getGridPanelAlignClass(index, 4);
			},
			ensureActivePanelVisible() {
				ensureEditFloatPanelVisible(this, {
					wrapSelector: '.g-image-quad-edit__cell-wrap',
					panelSelector: '.g-image-quad-edit__panel--float',
				});
			},
			getImageUrl(item) {
				return getImageQuadImageUrl(item?.image);
			},
			syncForm() {
				this.form = normalizeImageQuadData(this.data);
				this.heightDraft = this.form.imageHeight;
				this.radiusDraft = this.form.borderRadius;
				if (
					this.selectedIndex != null
					&& this.selectedIndex >= this.form.items.length
				) {
					this.selectedIndex = null;
				}
			},
			selectItem(index) {
				this.selectedIndex = index;
				this.ensureActivePanelVisible();
			},
			clearSelection() {
				this.selectedIndex = null;
				this.activePanel = '';
			},
			togglePanel(panel) {
				this.activePanel = this.activePanel === panel ? '' : panel;
				if (panel === 'height') {
					this.heightDraft = this.form.imageHeight;
				}
				if (panel === 'radius') {
					this.radiusDraft = this.form.borderRadius;
				}
				if (this.activePanel) {
					this.ensureActivePanelVisible();
				}
			},
			toggleFullWidth() {
				this.form.fullWidth = !this.form.fullWidth;
			},
			addBatch() {
				this.form.items.push(...createDefaultImageQuadItems(4));
				this.selectedIndex = this.form.items.length - 4;
			},
			addOne() {
				this.form.items.push(createDefaultImageQuadItem({}, this.form.items.length));
				this.selectedIndex = this.form.items.length - 1;
			},
			onItemDragStart(id) {
				handleEditListSortDragStart(this, id);
			},
			onSortDragOver(event) {
				handleEditListSortDragOver(event, this.$refs.editGrid, this);
			},
			onItemDragEnd() {
				handleEditListSortDragEnd(this, this.$refs.editGrid, this.form.items, this, (result) => {
					this.selectedIndex = resolveSelectedIndexAfterListMove(
						this.selectedIndex,
						result.fromIndex,
						result.toIndex,
					);
				});
			},
			deleteItem(index) {
				if (this.form.items.length <= 1) return;
				const removed = this.form.items[index];
				if (removed?.image?.local) {
					revokeLocalImageUrl(removed.image.previewUrl);
				}
				this.form.items.splice(index, 1);
				if (this.selectedIndex === index) {
					this.selectedIndex = null;
					this.activePanel = '';
				} else if (this.selectedIndex != null && this.selectedIndex > index) {
					this.selectedIndex -= 1;
				}
			},
			triggerImageSelect() {
				this.$refs.imageInput?.click();
			},
			async onImageSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !isLocalImageFile(file) || !this.selectedItem) return;
				if (file.size > MAX_LOCAL_IMAGE_SIZE) return;
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([file]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					this.selectedItem.image = replaceQrcodeNavCellImage(
						this.selectedItem.image,
						file,
						() => item,
					);
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			applyHeight() {
				this.form.imageHeight = Math.max(1, Number(this.heightDraft) || IMAGE_QUAD_DEFAULT_HEIGHT);
				this.activePanel = '';
			},
			applyRadius() {
				this.form.borderRadius = Math.max(0, Number(this.radiusDraft) || 0);
				this.activePanel = '';
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				const payload = normalizeImageQuadData({ ...this.form });
				this.$emit('confirm', payload);
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.edit-float-panel(g-image-quad-edit__panel);
	.edit-float-toolbar(g-image-quad-edit__toolbar);
	.edit-float-action(g-image-quad-edit__action);

	.g-image-quad-edit {
		min-height: 360px;
		padding: 4px 0 12px;
	}

	.g-image-quad-edit__actions {
		.edit-list-sort-actions-row();
		margin-bottom: 16px;
	}

	.g-image-quad-edit__actions-btns {
		.edit-list-sort-actions-btns();
	}

	.g-image-quad-edit__batch-btn {
		border: none;
		background: transparent;
		padding: 0;
		font-size: 13px;
		color: #285af9;
		cursor: pointer;

		&:hover {
			color: #1d4ed8;
		}
	}

	.g-image-quad-edit__grid {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 3px;
		width: 100%;
		overflow: visible;
		box-sizing: border-box;

		&.is-inset {
			padding: 0 8px;
		} 
	}

	.g-image-quad-edit__cell-wrap {
		position: relative;
		z-index: 0;
		overflow: visible;

		&.is-active {
			z-index: 2;
		}
	}

	.g-image-quad-edit__hint {
		.edit-list-sort-hint-text();
	}

	.g-image-quad-edit__cell {
		box-sizing: border-box;
		border: 2px dashed #fff;
		border-radius: 2px;
		cursor: grab;

		&:active {
			cursor: grabbing;
		}

		&.is-selected {
			border-color: #285af9;
		}
	}

	.g-image-quad-edit__thumb {
		width: 100%;
		height: auto;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		i {
			font-size: 24px;
			color: #bfbfbf;
		}
	}

	.g-image-quad-edit__sub-row {
		display: flex;
		align-items: center;
		flex-wrap: nowrap;
		gap: 8px;
		padding: 8px 12px 10px;
		border-top: 1px solid #f0f0f0;
		white-space: nowrap;
	}

	.g-image-quad-edit__sub-label {
		font-size: 13px;
		color: #666;
		white-space: nowrap;
		flex-shrink: 0;
	}

	.g-image-quad-edit__sub-input {
		width: 88px;
		flex-shrink: 0;
	}

	.g-image-quad-edit__sub-confirm {
		flex-shrink: 0;
		height: 28px;
		padding: 0 12px;
		border: none;
		border-radius: 4px;
		background: #285af9;
		color: #fff;
		font-size: 12px;
		line-height: 28px;
		white-space: nowrap;
		cursor: pointer;

		&:hover {
			background: #3868ff;
		}
	}

	.g-image-quad-edit__sub-default {
		font-size: 12px;
		color: #999;
		white-space: nowrap;
		flex-shrink: 0;
	}

	.g-image-quad-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-image-quad-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();

		.g-image-quad-edit-modal__ok {
			white-space: nowrap;
			flex-shrink: 0;

			&:hover,
			&:focus {
				background: #3868ff;
				border-color: #3868ff;
			}
		}
	}
</style>
